import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useToast,
  Spinner,
  Flex,
  Text,
  Collapse,
  Button,
  Stack,
  Link,
  Tooltip,
} from "@chakra-ui/react";
import { cqAPI } from "../../lib/Auth";
import moment from "moment";

function SmsIssues() {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState(() => new Set());
  const toast = useToast();

  const fetchIssues = async () => {
    try {
      setLoading(true);
      const response = await cqAPI.get("/issues/sms");
      const payload = response?.data?.data || response?.data || [];
      const sorted = [...payload].sort((a, b) => new Date(b.last) - new Date(a.last));
      setIssues(sorted);
    } catch (error) {
      console.error("Error fetching issues:", error);
      toast({ title: "Failed to load SMS issues", description: error.message, status: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIssues();
  }, []);

  const isExpanded = (key) => expandedRows.has(key);
  const toggleExpanded = (key) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const openInbox = (bid, uid) => {
    if (!bid || !uid) return;
    const url = `/inbox/${encodeURIComponent(uid)}?bid=${encodeURIComponent(bid)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const findUidForSid = (conversations, sid) => {
    if (!Array.isArray(conversations)) return null;
    const match = conversations.find((c) => c.sid === sid);
    return match ? match.uid : conversations[0]?.uid || null;
  };

  return (
    <Box p={4} maxHeight="100vh" width="100%" position="relative">
      <Box p={0} overflowY="auto" maxHeight="95vh" width="100%" position="relative">
        {loading ? (
          <Flex align="center" justify="center" py={10}>
            <Spinner mr={3} />
            <Text>Loading issues…</Text>
          </Flex>
        ) : (
          <TableContainer borderWidth="1px" borderRadius="lg">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Business</Th>
                  <Th>Phone</Th>
                  <Th isNumeric>Issues</Th>
                  <Th>Carrier</Th>
                  <Th>Last occurrence</Th>
                  <Th>Last SMS SID(s)</Th>
                </Tr>
              </Thead>
              <Tbody>
                {Array.isArray(issues) && issues.length > 0 ? (
                  issues.map((item) => {
                    const phone = item?._id?.phone || "—";
                    const carrier = item?._id?.carrier || "—";
                    const count = item?.count ?? 0;
                    const businessNames = Array.isArray(item?.business)
                      ? item.business.map((b) => b?.name).filter(Boolean).join(", ") || "—"
                      : "—";
                    const last = item?.last ? moment(item.last).format("MMM D, YYYY, h:mm A") : "—";
                    const sids = Array.isArray(item?.conversations)
                      ? item.conversations.map((c) => c.sid).filter(Boolean)
                      : [];
                    const rowKey = `${phone}-${carrier}-${item?.mapping?._id || item?.mapping?.bid || item?.last || Math.random()}`;
                    const expanded = isExpanded(rowKey);

                    return (
                      <Tr key={rowKey}>
                        <Td>{businessNames}</Td>
                        <Td>{phone}</Td>
                        <Td isNumeric>{count}</Td>
                        <Td>{carrier}</Td>
                        <Td>{last}</Td>
                        <Td>
                          {sids.length === 0 ? (
                            <Text color="gray.500">—</Text>
                          ) : (
                            <Box>
                              <Tooltip label="Open in inbox" hasArrow>
                                <Link
                                  fontFamily="mono"
                                  fontSize="xs"
                                  noOfLines={1}
                                  onClick={() => openInbox(item?.mapping?.bid, findUidForSid(item?.conversations, sids[0]))}
                                >
                                  {sids[0]}
                                </Link>
                              </Tooltip>
                              {sids.length > 1 && (
                                <>
                                  <Button
                                    variant="link"
                                    size="xs"
                                    mt={1}
                                    onClick={() => toggleExpanded(rowKey)}
                                  >
                                    {expanded ? "Show less" : `Show all (${sids.length})`}
                                  </Button>
                                  <Collapse in={expanded} animateOpacity>
                                    <Stack spacing={1} mt={2}>
                                      {sids.map((sid, idx) => (
                                        <Tooltip key={`${rowKey}-sid-${idx}`} label="Open in inbox" hasArrow>
                                          <Link
                                            fontFamily="mono"
                                            fontSize="xs"
                                            wordBreak="break-all"
                                            onClick={() => openInbox(item?.mapping?.bid, findUidForSid(item?.conversations, sid))}
                                          >
                                            {sid}
                                          </Link>
                                        </Tooltip>
                                      ))}
                                    </Stack>
                                  </Collapse>
                                </>
                              )}
                            </Box>
                          )}
                        </Td>
                      </Tr>
                    );
                  })
                ) : (
                  <Tr>
                    <Td colSpan={6}>
                      <Text fontSize="sm" color="gray.500">No SMS issues found.</Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}

export default SmsIssues;
